import React, { ReactNode, useEffect, useState } from "react"
import {
  Box, Flex, HStack, Text,
} from "@chakra-ui/react"
import { endOfDay } from "date-fns"
import { fromZonedTime, toZonedTime } from "date-fns-tz"
import _ from "lodash"
import { asDateDistance } from "utilities/strings"

const MINUTE = 60 * 1000

const remaining = (target: Date) => {
  const minutes = Math.max(0, Math.floor((target.getTime() - Date.now()) / MINUTE))

  return {
    days: Math.floor(minutes / (24 * 60)),
    hours: Math.floor((minutes % (24 * 60)) / 60),
    minutes: minutes % 60,
  }
}

type Props = {
  start: string
  end?: string | null
  timezone: string
}

// Without an end time, the event lasts until the end of its start day, in its own timezone.
export const endOfStartDay = (startDate: Date, timezone: string) => (
  fromZonedTime(endOfDay(toZonedTime(startDate, timezone)), timezone)
)

const Countdown = ({ start, end, timezone }: Props) => {
  const [, setTick] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => setTick((tick) => tick + 1), MINUTE)

    return () => clearInterval(interval)
  }, [])

  const startDate = new Date(start)
  if (Number.isNaN(startDate.getTime())) {
    return null
  }

  const endDate = end ? new Date(end) : endOfStartDay(startDate, timezone)
  if (Number.isNaN(endDate.getTime())) {
    return null
  }
  const now = Date.now()

  if (now < startDate.getTime()) {
    const { days, hours, minutes } = remaining(startDate)

    return (
      <Frame label="Countdown to Event">
        <HStack spacing={0} divider={<Box h={8} borderLeftWidth={1} alignSelf="center" />}>
          {([["Days", days], ["Hours", hours], ["Minutes", minutes]] as const).map(([unit, value]) => (
            <Box key={unit} px={4} textAlign="center">
              <Text fontSize="3xl" lineHeight="short">{value}</Text>
              <Text fontSize="2xs" fontWeight="semibold" textTransform="uppercase" color="gray.600">
                {unit}
              </Text>
            </Box>
          ))}
        </HStack>
      </Frame>
    )
  }

  return now <= endDate.getTime() ? (
    <Frame label="Event in Progress">
      <Status>Happening Now</Status>
    </Frame>
  ) : (
    <Frame label="Event Ended">
      <Status>{_.startCase(asDateDistance(endDate.toISOString(), true))}</Status>
    </Frame>
  )
}

const Frame = ({ label, children }: { label: string, children: ReactNode }) => (
  <Flex align="stretch" bg="white" borderWidth={1} borderRadius="md" overflow="hidden" flexShrink={0}>
    <Flex align="center" px={5} maxW={36} borderRightWidth={1}>
      <Text color="gray.600" fontStyle="italic" lineHeight="short">{label}</Text>
    </Flex>
    <Flex align="center" px={2} py={2} minH={16}>{children}</Flex>
  </Flex>
)

const Status = ({ children }: { children: ReactNode }) => (
  <Text px={4} fontSize="xl" fontWeight="semibold">{children}</Text>
)

export default Countdown
